import { Box, Button, Flex, SimpleGrid } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import Lightbox from 'react-image-lightbox';
import { ImageDerivative, MediaImage } from '../../generated/types';
import { trackEvent } from '../../utils/analytics';
import { pluralize } from '../../utils/helper';
import useDragDetection from '../../hooks/useDragDetection';
import CopyrightImage from './CopyrightImage';
import 'react-image-lightbox/style.css';

export type SliderImage = MediaImage & {
  teaserHuge: ImageDerivative;
  teaserSquare: ImageDerivative;
  tiny: ImageDerivative;
  full: ImageDerivative;
};

interface PageImagesEbikeAtlasProps {
  images: SliderImage[];
  vertical?: boolean;
  imageProp?: string;
  columns?: number;
  maxImages?: number;
}

export default function PageImagesEbikeAtlas({
  images,
  vertical,
  imageProp = 'teaserSquare',
  columns = 2,
  maxImages,
}: PageImagesEbikeAtlasProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (isModalOpen) {
      trackEvent('HotelDetail', 'Open', 'Photo Lightbox');
    }
  }, [isModalOpen]);

  // Filter null images
  images = images.filter((i) => i);

  const allImages = [...images];

  if (maxImages && images.length > maxImages) {
    images = images.slice(0, maxImages);
  }

  if (images.length === 0) {
    return null;
  }

  const { handleMouseDown, dragging } = useDragDetection();

  const handleChildClick = (i: number) => (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (dragging) {
      e.preventDefault();
      return;
    }

    setIndex(i);
    setIsModalOpen(true);
  };

  let imageCaption: string[] = [];

  if (
    allImages[index].useNameAsCaption &&
    allImages[index].name &&
    allImages[index].name !== ''
  ) {
    imageCaption.push(allImages[index].name!);
  }

  if (allImages[index].copyright) {
    imageCaption.push(`© ${allImages[index].copyright}`);
  }

  return (
    <>
      {isModalOpen && (
        <Lightbox
          {...{
            'data-track-content': '',
            'data-content-piece': 'Hotel Bild Galerie',
            'data-content-name': 'Hotel Detail',
          }}
          imageCaption={imageCaption.join('  -  ')}
          mainSrc={allImages[index].full.url}
          nextSrc={allImages[(index + 1) % allImages.length].full.url}
          prevSrc={
            allImages[(index + allImages.length - 1) % allImages.length].full
              .url
          }
          onCloseRequest={() => setIsModalOpen(false)}
          onMovePrevRequest={() =>
            setIndex((index + allImages.length - 1) % allImages.length)
          }
          onMoveNextRequest={() => setIndex((index + 1) % allImages.length)}
        />
      )}
      <Flex
        direction={{
          base: 'column',
          md: vertical ? 'column' : 'row',
        }}
      >
        <Box flex="2" pos="relative">
          <CopyrightImage
            borderRadius="lg"
            alt={images[0].imageAlt || ''}
            title={images[0].imageTitle || ''}
            copyright={images[0].copyright || ''}
            cursor="pointer"
            width="100%"
            src={images[0].teaserHuge.url}
            onClick={() => {
              setIndex(0);
              setIsModalOpen(true);
            }}
          />

          <Box pos="absolute" bottom="0" left="0" p="4">
            <Button
              size="sm"
              onClick={() => {
                setIndex(0);
                setIsModalOpen(true);
              }}
            >
              Fotos ansehen ({allImages.length}{' '}
              {pluralize(images.length, 'Foto', 'Fotos')})
            </Button>
          </Box>
        </Box>
        <SimpleGrid
          columns={columns}
          flex="1"
          mt={{
            base: 4,
            md: vertical ? 4 : 0,
          }}
          ml={{
            base: 0,
            md: vertical ? 0 : 8,
          }}
          spacing={{
            base: 4,
            md: 8,
          }}
          templateRows={{
            base: images.length <= columns + 1 ? '1fr' : '1fr 1fr',
            md: vertical && images.length <= columns + 1 ? '1fr' : '1fr 1fr',
          }}
        >
          {images.length > 1 && (
            <Box pos="relative">
              <CopyrightImage
                borderRadius="lg"
                width="100%"
                alt={images[1].imageAlt || ''}
                title={images[1].imageTitle || ''}
                copyright={images[1].copyright || ''}
                cursor="pointer"
                src={images[1][imageProp]?.url}
                onClick={() => {
                  setIndex(1);
                  setIsModalOpen(true);
                }}
              />
            </Box>
          )}

          {images.length > 2 && (
            <Box pos="relative">
              <CopyrightImage
                borderRadius="lg"
                width="100%"
                alt={images[2].imageAlt || ''}
                title={images[2].imageTitle || ''}
                copyright={images[2].copyright || ''}
                cursor="pointer"
                src={images[2][imageProp]?.url}
                onClick={() => {
                  setIndex(2);
                  setIsModalOpen(true);
                }}
              />
            </Box>
          )}

          {images.length > 3 && (
            <Box pos="relative">
              <CopyrightImage
                borderRadius="lg"
                width="100%"
                alt={images[3].imageAlt || ''}
                title={images[3].imageTitle || ''}
                copyright={images[3].copyright || ''}
                cursor="pointer"
                src={images[3][imageProp]?.url}
                onClick={() => {
                  setIndex(3);
                  setIsModalOpen(true);
                }}
              />
            </Box>
          )}

          {images.length > 4 && (
            <Box pos="relative">
              <CopyrightImage
                borderRadius="lg"
                width="100%"
                alt={images[4].imageAlt || ''}
                title={images[4].imageTitle || ''}
                copyright={images[4].copyright || ''}
                cursor="pointer"
                src={images[4][imageProp]?.url}
                onClick={() => {
                  setIndex(4);
                  setIsModalOpen(true);
                }}
              />
            </Box>
          )}
        </SimpleGrid>
      </Flex>
      {/*{images.length > 1 && (*/}
      {/*  <Box marginTop={1}>*/}
      {/*    <Slider*/}
      {/*      variableWidth*/}
      {/*      swipeToSlide={true}*/}
      {/*      infinite={false}*/}
      {/*      arrows={false}*/}
      {/*      dots={false}*/}
      {/*    >*/}
      {/*      {images.map((image, i) => (*/}
      {/*        <Box*/}
      {/*          overflow="hidden"*/}
      {/*          width="100px"*/}
      {/*          key={i}*/}
      {/*          onMouseDownCapture={handleMouseDown}*/}
      {/*          onClickCapture={handleChildClick(i)}*/}
      {/*          cursor="pointer"*/}
      {/*          marginRight={1}*/}
      {/*          _focus={{*/}
      {/*            boxShadow: 'none',*/}
      {/*            outline: 'none',*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          <Img*/}
      {/*            borderRadius="lg"*/}
      {/*            src={image.tiny?.url || ''}*/}
      {/*            alt={image.imageAlt || ''}*/}
      {/*            title={image.imageTitle || ''}*/}
      {/*            width={100}*/}
      {/*            height={100}*/}
      {/*          />*/}
      {/*        </Box>*/}
      {/*      ))}*/}
      {/*    </Slider>*/}
      {/*  </Box>*/}
      {/*)}*/}
    </>
  );
}
